import apiClient from "../Api/apiConfig";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FileText } from "lucide-react";
import FileUpload from "../components/grievance/FileUpload";
import "../styles/GrievancePage.css";
import { generateGrievanceReference } from "../utils/reference";
import { useAuth } from "../lib/auth";

// ✅ Schéma de validation avec Zod incluant le champ agencyId
const grievanceSchema = z.object({
  title: z.string().min(5, "Le titre doit contenir au moins 5 caractères"),
  description: z
    .string()
    .min(20, "La description doit contenir au moins 20 caractères"),
  theme: z.string().nonempty("Veuillez sélectionner un thème"),
  landlordId: z.string().nonempty("Veuillez sélectionner un propriétaire"),
  agencyId: z.string().nonempty("Veuillez sélectionner une agence"),
});

export default function GrievancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);

  const [themes, setThemes] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [allAgencies, setAllAgencies] = useState([]); // Toutes les agences disponibles
  const [agencies, setAgencies] = useState([]); // Agences filtrées selon le bailleur sélectionné
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingThemes, setLoadingThemes] = useState(true);
  const [loadingLandlords, setLoadingLandlords] = useState(true);
  const [loadingAgencies, setLoadingAgencies] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(grievanceSchema),
  });

  // Surveille la sélection du propriétaire dans le formulaire
  const selectedLandlordId = watch("landlordId");

  // 🔐 Initialisation de l'utilisateur connecté
  useEffect(() => {
    if (user) {
      console.log("Utilisateur connecté :", user);
      setCurrentUser(user);
    }
  }, [user]);

  // Utilitaire pour détecter qu'on a reçu du HTML (index.html) au lieu de JSON
  const isHtmlResponse = (data) =>
    typeof data === "string" && data.trim().startsWith("<!DOCTYPE html>");

  // 🔹 Chargement des thèmes depuis l'API
  const fetchThemes = async () => {
    try {
      const response = await apiClient.get("/themes");
      console.log("Réponse brute /themes :", response.data);

      if (isHtmlResponse(response.data)) {
        console.error(
          "❌ /themes renvoie du HTML : ton appel va sur le FRONT (5173) au lieu du BACK (5000). Vérifie baseURL et proxy."
        );
        setThemes([]);
        return;
      }

      const data = response.data;

      if (Array.isArray(data)) {
        setThemes(data);
      } else if (Array.isArray(data.themes)) {
        setThemes(data.themes);
      } else {
        console.error("Format de données /themes inattendu :", data);
        setThemes([]);
      }
    } catch (error) {
      console.error("Erreur chargement thèmes :", error);
      setErrorMessage(
        error.message || "Impossible de charger les thèmes."
      );
      setThemes([]);
    } finally {
      setLoadingThemes(false);
    }
  };

  // 🔹 Chargement des bailleurs depuis l'API
  const fetchLandlords = async () => {
    try {
      const response = await apiClient.get("/landlords");
      console.log("Réponse brute /landlords :", response.data);

      if (isHtmlResponse(response.data)) {
        console.error(
          "❌ /landlords renvoie du HTML : ton appel va sur le FRONT (5173) au lieu du BACK (5000)."
        );
        setLandlords([]);
        return;
      }

      const data = response.data;
      if (Array.isArray(data)) {
        setLandlords(data);
      } else if (Array.isArray(data.landlords)) {
        setLandlords(data.landlords);
      } else {
        console.error("Format de données /landlords inattendu :", data);
        setLandlords([]);
      }
    } catch (error) {
      console.error("Erreur chargement bailleurs :", error);
      setErrorMessage(
        error.message || "Impossible de charger les propriétaires."
      );
      setLandlords([]);
    } finally {
      setLoadingLandlords(false);
    }
  };

  // 🔹 Chargement de toutes les agences depuis l'API
  const fetchAllAgencies = async () => {
    try {
      const response = await apiClient.get("/agencies");
      console.log("Réponse brute /agencies :", response.data);

      if (isHtmlResponse(response.data)) {
        console.error(
          "❌ /agencies renvoie du HTML : ton appel va sur le FRONT (5173) au lieu du BACK (5000)."
        );
        setAllAgencies([]);
        return;
      }

      const data = response.data;
      if (Array.isArray(data)) {
        setAllAgencies(data);
      } else if (Array.isArray(data.agencies)) {
        setAllAgencies(data.agencies);
      } else {
        console.error("Format de données /agencies inattendu :", data);
        setAllAgencies([]);
      }
    } catch (error) {
      console.error("Erreur chargement agences :", error);
      setErrorMessage(
        error.message || "Impossible de charger les agences."
      );
      setAllAgencies([]);
    } finally {
      setLoadingAgencies(false);
    }
  };

  useEffect(() => {
    fetchThemes();
    fetchLandlords();
    fetchAllAgencies();
  }, []);

  // 🔹 Filtrer les agences en fonction du bailleur sélectionné
  useEffect(() => {
    if (selectedLandlordId) {
      const filteredAgencies = allAgencies.filter(
        (agency) =>
          String(agency.landlord_id) === String(selectedLandlordId)
      );
      console.log(
        "Agences filtrées pour le bailleur",
        selectedLandlordId,
        ":",
        filteredAgencies
      );
      setAgencies(filteredAgencies);
      setValue("agencyId", ""); // Réinitialisation du champ agence
    } else {
      setAgencies([]);
      setValue("agencyId", "");
    }
  }, [selectedLandlordId, allAgencies, setValue]);

  // 🔹 Soumission du formulaire
  const onSubmit = async (data) => {
    console.log("Données du formulaire :", data);
    setIsSubmitting(true);
    setErrorMessage("");

    const authToken =
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken");

    if (!authToken || !currentUser) {
      setErrorMessage(
        "Vous devez être connecté pour soumettre un signalement."
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const landlord = landlords.find(
        (l) => String(l.id) === String(data.landlordId)
      );
      const agency = agencies.find(
        (a) => String(a.id) === String(data.agencyId)
      );

      console.log("Bailleur sélectionné :", landlord);
      console.log("Agence sélectionnée :", agency);

      if (!landlord?.email) {
        setErrorMessage("L'email du bailleur est manquant.");
        setIsSubmitting(false);
        return;
      }
      if (!agency?.email) {
        setErrorMessage("L'email de l'agence est manquant.");
        setIsSubmitting(false);
        return;
      }

      const grievance = {
        title: data.title.trim(),
        description: data.description.trim(),
        theme_id: data.theme,
        reference: generateGrievanceReference(),
        user_id: currentUser.id,
        user_email: currentUser.email,
        landlord_id: data.landlordId,
        landlord_email: landlord.email,
        agency_id: data.agencyId,
        agency_email: agency.email,
        statuts: "new",
      };

      console.log("Payload de la réclamation :", grievance);

      const formData = new FormData();
      Object.keys(grievance).forEach((key) =>
        formData.append(key, grievance[key])
      );
      attachments.forEach((file) =>
        formData.append("attachments", file)
      );

      const response = await apiClient.post("/grievances", formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("Réponse du backend :", response.data);

      alert("Votre signalement a été enregistré avec succès.");
      setAttachments([]);
      setTimeout(() => {
        navigate("/");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la soumission :", error);
      const errMsg =
        error.response?.data?.message ||
        "Une erreur est survenue lors de la soumission.";
      setErrorMessage(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grievance-page">
      <div className="form-container">
        <div className="header">
          <FileText className="icon" />
          <h2>Signaler un problème</h2>
          <p>
            Décrivez votre problème pour que nous puissions vous aider
            efficacement.
          </p>
        </div>

        {errorMessage && (
          <p className="error-message">{errorMessage}</p>
        )}

        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          {/* Champ Titre */}
          <div className="form-group">
            <label htmlFor="title">Titre</label>
            <input
              id="title"
              {...register("title")}
              type="text"
              placeholder="Ex : Panne d'ascenseur au 3ème étage"
              className={`input ${
                errors.title ? "input-error" : ""
              }`}
            />
            {errors.title && (
              <p className="error-message">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Champ Description */}
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              {...register("description")}
              rows="4"
              placeholder="Décrivez le problème en détail..."
              className={`textarea ${
                errors.description ? "input-error" : ""
              }`}
            />
            {errors.description && (
              <p className="error-message">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Champ Thème */}
          <div className="form-group">
            <label htmlFor="theme">Thème</label>
            <select
              id="theme"
              {...register("theme")}
              disabled={loadingThemes}
              className={`select ${
                errors.theme ? "input-error" : ""
              }`}
            >
              <option value="">
                {loadingThemes
                  ? "Chargement des thèmes..."
                  : "Sélectionnez un thème"}
              </option>

              {Array.isArray(themes) &&
                themes.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.name}
                  </option>
                ))}
            </select>
            {errors.theme && (
              <p className="error-message">
                {errors.theme.message}
              </p>
            )}
          </div>

          {/* Champ Propriétaire */}
          <div className="form-group">
            <label htmlFor="landlordId">Propriétaire</label>
            <select
              id="landlordId"
              {...register("landlordId")}
              disabled={loadingLandlords}
              className={`select ${
                errors.landlordId ? "input-error" : ""
              }`}
            >
              <option value="">
                {loadingLandlords
                  ? "Chargement des propriétaires..."
                  : "Sélectionnez un propriétaire"}
              </option>
              {Array.isArray(landlords) &&
                landlords.map((landlord) => (
                  <option key={landlord.id} value={landlord.id}>
                    {landlord.name}
                  </option>
                ))}
            </select>
            {errors.landlordId && (
              <p className="error-message">
                {errors.landlordId.message}
              </p>
            )}
          </div>

          {/* Champ Agence */}
          {selectedLandlordId && (
            <div className="form-group">
              <label htmlFor="agencyId">Agence</label>
              <select
                id="agencyId"
                {...register("agencyId")}
                disabled={loadingAgencies}
                className={`select ${
                  errors.agencyId ? "input-error" : ""
                }`}
              >
                <option value="">
                  {loadingAgencies
                    ? "Chargement des agences..."
                    : "Sélectionnez une agence"}
                </option>
                {Array.isArray(agencies) &&
                  agencies.map((agency) => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
              </select>
              {errors.agencyId && (
                <p className="error-message">
                  {errors.agencyId.message}
                </p>
              )}
            </div>
          )}

          {/* Champ Fichiers joints */}
          <div className="form-group">
            <label htmlFor="attachments">Photos / Documents</label>
            <FileUpload onFilesChange={setAttachments} />
          </div>

          {/* Bouton de soumission */}
          <div className="form-group">
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Envoi en cours..."
                : "Soumettre le signalement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
